import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '../../components/Breadcrumb';
import Heading from '../../components/Heading';
import { BASE_URL } from '../../routes/routeConstant';
import PromoCard from '../ServiceView/component/PromoCard';
import Skeleton from '../../components/ui/skeleton';
import usePromotion from '../../stores/usePromo';
import { LanguageContext } from '../../contexts/LanguageContext';
import InfiniteScrollObserver from '../../components/InfiniteScrollObserver';

export default function AllPromo() {
  const { t } = useTranslation();
  const { language } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const lang = language === 'id';
  const breadcrumbPath = [{ name: t('navigation.home'), link: BASE_URL }, { name: t('navigation.deals') }];
  const { fetchLoading, fetchPromotions, hasFetched, promotions, page, hasMore, isLoadingNextPage } = usePromotion();

  useEffect(() => {
    if (!hasFetched && !fetchLoading) {
      fetchPromotions(1, true);
    }
  }, [fetchPromotions, fetchLoading, hasFetched]);

  const memoizedPromotions = useMemo(() => promotions, [promotions]);

  const handleLoadMore = () => {
    fetchPromotions(page + 1, false);
  };

  return (
    <section className="flex flex-col items-center h-full w-full gap-4 p-2 my-4 md:my-8">
      <div className="flex flex-col items-center gap-1">
        <Heading>{t('navigation.deals')}</Heading>
        <Breadcrumb backStatus="hidden" paths={breadcrumbPath} />
      </div>
      <p className="text-center mb-4 sm:text-base text-sm md:w-[70%] w-[90%]">{t('tagline.deals')}</p>
      {fetchLoading ? (
        <div
          key="sekeleton"
          className="flex animate-fade-in-up flex-wrap overflow-hidden gap-2 justify-center w-full sm:w-[98%]"
        >
          {Array.from({ length: 10 }).map(() => (
            <Skeleton key={Math.random()} className="sm:w-56 w-40 shadow-md sm:h-64 h-48" />
          ))}
        </div>
      ) : (
        <div
          key="card-promo"
          className="flex animate-fade-in-up flex-wrap overflow-hidden gap-2 justify-center w-full sm:w-[98%]"
        >
          {memoizedPromotions.map((prm) => (
            <PromoCard
              key={prm.promo_id}
              imageUrl={prm.image_prm || '/assets/no-data-image.svg'}
              service={lang ? prm.service.name_srv_id : prm.service.name_srv_en || prm.service.name_srv_id}
              name={lang ? prm.name_prm : prm.name_prm_en || prm.name_prm}
              price={prm.price_prm}
              className="sm:w-56 w-40"
            />
          ))}
          {isLoadingNextPage && (
            <section className="grid sm:grid-cols-[repeat(auto-fill,_minmax(12rem,_1fr))] grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-2 w-full col-span-full">
              <Skeleton className="sm:h-64 h-48" />
              <Skeleton className="sm:h-64 h-48" />
              <Skeleton className="sm:h-64 h-48" />
            </section>
          )}
          <InfiniteScrollObserver
            hasMore={hasMore}
            isLoading={fetchLoading || isLoadingNextPage}
            onLoadMore={handleLoadMore}
          />
        </div>
      )}
    </section>
  );
}
